import omit from 'lodash/omit';
import type { LocalIssueId } from '@atlassian/jira-polaris-domain-idea/src/idea/types.tsx';
import type { IssueTypeId } from '@atlassian/jira-shared-types/src/general.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { StoreActionApi } from '@atlassian/react-sweet-state';

import type { CreateIdeasBulkResponse } from '@atlassian/jira-polaris-remote-issue/src/services/jira/create-idea-rest-bulk/types.tsx';
import {
	type IssueCreatedPropertyItem,
	IssueLastActionCreate,
	type Props,
	type State,
} from '../../types.tsx';

import { incrementOpenUpdateCounter } from '../real-time/index.tsx';
import { getFieldMappings } from '../../selectors/fields.tsx';
import { updateFieldValues } from '../update-field-value/index.tsx';
import { getFieldsToUpdate, getFieldsToUpdatePayload } from '../create-issue/utils.tsx';
import { getFilteredIssueIds } from '../../selectors/filters.tsx';
import {
	generateBulkUpdatePayload,
	getAnchorKey,
	getNewSortedIds,
	updateLocalIssueIdToJiraId,
	updatePropertiesWithResponse,
	updateStateProperties,
} from './utils.tsx';

/** save Bulk Issue action - creates issue in Jira and also ranks it accordingly. */
export const saveIssueBulk =
	(
		issuesMap: Record<LocalIssueId, string>,
		issueTypeId: IssueTypeId,
		createdProperty: IssueCreatedPropertyItem,
		onIssueSaved?: (ids: string[]) => void,
		onIssueSaveError?: (error: Error) => void,
		onCreatedIssueFiltered?: (ids: LocalIssueId[]) => void,
		deferJiraEvents?: () => void,
		resumeJiraEvents?: () => void,
	) =>
	({ getState, setState, dispatch }: StoreActionApi<State>, props: Props) => {
		setState({ lastUpdatedAction: undefined });
		const { issuesRemote, isRankingEnabled, rankField } = props;
		const newLocalIds = Object.keys(issuesMap);

		deferJiraEvents?.();
		dispatch(incrementOpenUpdateCounter(newLocalIds));

		// Building request payload
		const payloadByLocalIssueId = generateBulkUpdatePayload(issuesMap);

		// Optimistic state update
		const state = getState();
		const optimisticProperties = updateStateProperties(
			state.properties,
			issuesMap,
			createdProperty,
		);

		// Anchor before means that item should be after
		const rankAfter = createdProperty.anchorBefore;
		const rankBefore = createdProperty.anchorAfter;
		const optimisticIds = getNewSortedIds(state.ids, newLocalIds, rankBefore, rankAfter);

		setState({
			properties: optimisticProperties,
			ids: optimisticIds,
		});

		const lastId = newLocalIds.at(-1) || '';
		const anchorAfterIssueKey = getAnchorKey(optimisticProperties, lastId, optimisticIds);

		// Request
		return issuesRemote
			.createIssueBulk({
				issueTypeId,
				payloadByLocalIssueId,
				isRankingEnabled,
				rankBeforeIdeaKey: anchorAfterIssueKey,
				rankCustomFieldId: rankField,
				onBatchCreated: (idMap) => updateLocalIssueIdToJiraId(idMap, getState, setState),
			})
			.then((response: CreateIdeasBulkResponse) => {
				// Update state with properties from response
				const stateForUpdateOperation = getState();
				const newCreated = omit(stateForUpdateOperation.properties.created, newLocalIds);
				const properties = {
					...stateForUpdateOperation.properties,
					created: newCreated,
				};
				const fieldMappings = getFieldMappings(state, props);
				const updatedProperties = updatePropertiesWithResponse(
					fieldMappings,
					newLocalIds,
					response,
					properties,
				);

				setState({
					properties: updatedProperties,
				});

				setState({ lastUpdatedIssueIds: newLocalIds });
				const issueIds = Object.values(response).map((jiraResponse) => jiraResponse.id);
				onIssueSaved?.(issueIds);

				// In case of grouping update issues with grouped values
				const fieldsToUpdate: Record<string, unknown> = getFieldsToUpdate(createdProperty);

				if (Object.keys(fieldsToUpdate).length > 0) {
					dispatch(
						updateFieldValues({
							fields: getFieldsToUpdatePayload(fieldsToUpdate),
							localIssueIds: newLocalIds,
						}),
					);
				}
				setState({ lastUpdatedAction: IssueLastActionCreate });
				// Handle filtering
				if (onCreatedIssueFiltered) {
					const visibleIssueIds = getFilteredIssueIds(getState(), props);
					const createdFilteredIds = newLocalIds.filter((id) => !visibleIssueIds.includes(id));
					if (createdFilteredIds.length > 0) {
						onCreatedIssueFiltered(createdFilteredIds);
					}
				}
				resumeJiraEvents?.();
			})
			.catch((error) => {
				resumeJiraEvents?.();
				props.onIssueCreationFailed(error, issueTypeId);

				onIssueSaveError && onIssueSaveError(error);
			});
	};
